import { jsx as _jsx, jsxs as _jsxs } from "preact/jsx-runtime";
import { useState, useEffect, useCallback, useContext, useRef } from 'preact/hooks';
import { SDKContext } from '../context';
import { useIsMouMobile } from '../utils/responsive';
import { debounce, hasTextContent } from '../utils';
import { OfferCard } from './OfferCard';
/**
 * Grid component that displays multiple offer cards in MOU layout.
 *
 * Equal tile heights and bottom-aligned CTAs (desktop only, >980px) are handled by CSS:
 * the row stretches (`align-items: stretch`) so every card matches the tallest,
 * and each card is a flex column whose content area grows (`flex: 1`) while the
 * CTA container is pushed down (`margin-top: auto`). At 980px and below, cards
 * use natural height. No JS height measurement.
 */
export function OfferGrid({ offers, settings, styles, apiPayload, onOfferClick, }) {
    const ctx = useContext(SDKContext);
    const isMobile = useIsMouMobile();
    const [selectedOffers, setSelectedOffers] = useState(new Set());
    const isUSPEnabled = settings.enable_user_selected_perks &&
        settings.enable_usp &&
        apiPayload?.pub_user_id;
    // Initialize selected offers for USP via per-instance SDKContext
    useEffect(() => {
        if (!isUSPEnabled || !ctx)
            return;
        const { sdk } = ctx;
        // Restore prefetch-cached selections — reused_session was set by SDK.fetchAds()
        if (sdk.reused_session) {
            setSelectedOffers(new Set(sdk.mouState.selectedOffers));
            sdk.dispatchUSPEvent();
            return;
        }
        // Auto-select all offers when the publisher setting requires it and this is
        // a fresh session (not a prefetch restore).
        if (settings.usp_all_offers_checked && !sdk.reused_session) {
            const allOfferIds = offers.map(offer => offer.campaign_id ?? offer.id);
            setSelectedOffers(new Set(allOfferIds));
            sdk.mouState = { selectedOffers: [...allOfferIds] };
            sdk.dispatchUSPEvent();
        }
    }, [isUSPEnabled, settings.usp_all_offers_checked, offers, ctx]);
    // Keep a live ref to ctx so the debounced dispatch (created once) sees the current SDK instance.
    const ctxRef = useRef(ctx);
    useEffect(() => { ctxRef.current = ctx; }, [ctx]);
    // Debounce the session-level PUT so rapid toggles collapse to one network call.
    const debouncedDispatchRef = useRef(debounce(() => { ctxRef.current?.sdk.dispatchUSPEvent(); }, 1000));
    const handleCheckboxChange = useCallback((offerId, isSelected) => {
        setSelectedOffers(prev => {
            const newSelected = new Set(prev);
            if (isSelected) {
                newSelected.add(offerId);
            }
            else {
                newSelected.delete(offerId);
            }
            return newSelected;
        });
        // SDK state + debounced per-offer API call — outside the updater to avoid side effects in a pure fn.
        if (ctx) {
            isSelected ? ctx.sdk.addSelectedOffer(offerId) : ctx.sdk.removeSelectedOffer(offerId);
        }
        debouncedDispatchRef.current();
    }, [ctx]);
    // Check if all offers lack T&C and PerksWallet to hide additional CTA container
    const shouldHideAdditionalCTAs = () => {
        const adpxUtils = window.AdpxUtils;
        const allLackTnC = offers.every(offer => {
            if (!adpxUtils?.hasTextContent) {
                return (!offer.terms_and_conditions ||
                    offer.terms_and_conditions.trim() === '');
            }
            return !adpxUtils.hasTextContent(offer.terms_and_conditions || '');
        });
        const noPerksWallet = !settings.enable_perkswallet && !settings.enable_pwaas_webhook;
        return allLackTnC && noPerksWallet;
    };
    // Grid styling
    const gridGap = styles.offerwall?.mou_tile_gap || 6;
    const gridStyles = {
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        // Desktop only (>980px): stretch makes every card match the tallest one.
        alignItems: isMobile ? 'center' : 'stretch',
        gap: `${gridGap}px`,
        padding: isMobile ? '0 0 12px 0' : '0 0 16px 0',
        overflowY: isMobile ? 'visible' : 'auto',
        height: settings.embedded?.enabled && isMobile ? '100%' : 'auto',
        maxHeight: settings.embedded?.enabled && isMobile ? '100%' : 'none',
        minHeight: settings.embedded?.enabled && isMobile ? '100%' : 'auto',
    };
    // Add this calculation in OfferGrid component
    const hasAnyPerksWallet = offers.some(offer => {
        const isEnabled = ((settings.enable_pwaas_webhook && apiPayload?.pub_user_id) ||
            settings.enable_perkswallet) &&
            offer.campaign?.perkswallet_enabled &&
            settings.perkswallet_show_cta;
        return isEnabled;
    });
    // If any tile shows a T&C link, reserve that row on every tile so all CTAs
    // line up at the same height; if no tile has T&C, the row isn't rendered.
    const hasAnyTnC = offers.some(offer => hasTextContent(offer.terms_and_conditions));
    return (_jsxs("div", { id: 'adpx_mou-offers-container', style: gridStyles, children: [offers.map((offer, index) => (_jsx(OfferCard, { offer: offer, settings: settings, styles: styles, apiPayload: apiPayload, onOfferClick: onOfferClick, ...(isUSPEnabled ? { onCheckboxChange: handleCheckboxChange } : {}), isSelected: selectedOffers.has(offer.campaign_id ?? offer.id), showImage: styles.offerText?.show_image !== false, index: index, totalOffers: offers.length, hasAnyPerksWallet: hasAnyPerksWallet, hasAnyTnC: hasAnyTnC }, offer.id))), shouldHideAdditionalCTAs() && (_jsx("style", { children: `
            #adpx_mou-additional-cta-container {
              display: none !important;
            }
            .adpx-mou_modal-content-container {
              padding-bottom: 8px !important;
            }
          ` }))] }));
}
